import { isBoolean, isEmpty, isNil, isNumber, omit } from 'lodash';

import { guideRecord } from '@issue-detail/components/IssueDialog/api';
import { usePromise } from '@issue-detail/utils/usePromise';
import { useVuex } from '@issue-detail/utils/useVuex';
import { computed, ref, Ref, watch } from '@vue/composition-api';

import { getIssueLayout } from '../Api/issue';
import { IssueAssembleResponse } from '../Model/Issue';
import { ActionStore } from './action';
import { FieldsStore } from './fields';
import { ProjectStore } from './project';
import { SprintStore } from './sprint';

export const useIssueType = (
  actionStore: ActionStore,
  issueAssemble: Ref<IssueAssembleResponse | null>,
  projectStore: ProjectStore,
  sprintStore: SprintStore,
  lazyFieldStore: () => unknown // 特殊情况，需要懒加载的字段，类型也是，不然会有循环依赖
) => {
  const vuex = useVuex();

  //#region change issueType
  let targetIssueTypeId: number | null = null;
  const isShowChangeIssueTypeConfigDialog = ref(false);

  const diffOfIssueType = async (issueTypeId: number) => {
    const fieldStore = lazyFieldStore() as FieldsStore;
    const oldData = Object.keys(fieldStore.fieldCodeMap.value).map(
      (fieldCode) => ({
        fieldCode: fieldCode,
        value: fieldStore.fieldValues.value[fieldCode]
      })
    );
    // 排除掉为空的字段
    const oldFields = oldData
      .filter(
        (item) =>
          !(
            isNil(item.value) ||
            (isEmpty(item.value) &&
              !isBoolean(item.value) &&
              !isNumber(item.value))
          )
      )
      .map((item) => item.fieldCode);

    const newLayout = await getIssueLayout(
      projectStore.currentProjectId.value!,
      issueTypeId
    );

    const newFields = newLayout
      .flatMap((area) =>
        area.groupList.flatMap((group) =>
          group.issueFieldOrdinateVOList.flatMap((o) => o.issueFieldInfoVo)
        )
      )
      .map((item) => item.fieldCode);

    const noExistList = oldFields.filter((f) => !newFields.includes(f));
    return !noExistList.length;
  };

  const requestChangeIssueType = async (issueTypeId: number) => {
    if (actionStore.action.value && issueTypeId !== currentTypeId.value) {
      const ignoreSwitchIssueTypeTips =
        vuex.getters['guide/isDoneGuideByType'](6);
      if (
        ignoreSwitchIssueTypeTips ||
        actionStore.action.value.type === 'Create'
      ) {
        await changeIssueType(issueTypeId);
      } else {
        const isSame = await diffOfIssueType(issueTypeId);
        if (isSame) {
          await changeIssueType(issueTypeId);
        } else {
          targetIssueTypeId = issueTypeId;
          isShowChangeIssueTypeConfigDialog.value = true;
        }
      }
    }
  };

  const confirmChangeIssueType = async (ignoreTips: boolean) => {
    if (!targetIssueTypeId) {
      return;
    }
    await changeIssueType(targetIssueTypeId);
    targetIssueTypeId = null;
    isShowChangeIssueTypeConfigDialog.value = false;
    if (ignoreTips) {
      await guideRecord(6);
      await vuex.dispatch('guide/queryGuideInfo', true);
    }
  };

  const changeIssueType = async (issueTypeId: number) => {
    if (actionStore.action.value) {
      const issueType = issueTypes.value.find(
        (item) => item.id === issueTypeId
      );
      if (!issueType) {
        return;
      }

      const fieldStore = lazyFieldStore() as FieldsStore;
      if (actionStore.action.value.type === 'Edit') {
        await fieldStore.updateIssueImmediately(
          actionStore.action.value.issueId,
          {
            issueTypeId
          }
        );
        actionStore.editIssue(
          actionStore.action.value.issueId,
          undefined,
          undefined,
          true
        );
        actionStore.markChanged();
      } else if (actionStore.action.value.type === 'Create') {
        actionStore.createIssue(
          actionStore.action.value.projectId,
          sprintStore.currentSprintId.value ?? undefined,
          issueTypeId,
          omit(
            {
              ...actionStore.action.value.defaultData,
              ...fieldStore.fieldValues.value
            },
            ['projectId', 'sprint', 'issueTypeId']
          ),
          undefined,
          actionStore.isContinueCreate.value
        );
      }
    }
  };

  const issueTypes = computed(() => issueAssemble.value?.issueTypeVOList || []);

  const currentTypeId = computed(() => {
    if (actionStore.action.value?.type === 'PreviewType') {
      return actionStore.action.value.issueTypeId;
    }
    if (actionStore.action.value?.type === 'Create') {
      return (
        actionStore.action.value.issueTypeId ||
        issueTypes.value.find((i) => i.recommended)?.id ||
        issueTypes.value[0]?.id
      );
    }
    if (actionStore.action.value?.type === 'Edit') {
      return issueAssemble.value?.issueVO?.data.issueTypeId as number;
    }
  });

  return {
    issueTypes,
    currentIssueType: computed(() =>
      currentTypeId.value
        ? issueTypes.value.find((t) => t.id === currentTypeId.value)
        : undefined
    ),
    currentTypeId,
    requestChangeIssueType,
    confirmChangeIssueType,
    isShowChangeIssueTypeConfigDialog
  };
};

export type IssueTypeStore = ReturnType<typeof useIssueType>;
